import type { FC } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type {
  ContractManagementResponse,
  ShipperResponse,
} from 'src/apis/contract-management/types';
import type { ShipperPlatformIdsRequest } from 'src/apis/monolith/use-shipper-platform-ids';
import { useOrganisationDetails } from 'src/apis/organisation-service/use-organisation-details';
import type { OrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import { Link } from 'src/components/navigation/link';
import { Tabs } from 'src/components/navigation/tabs';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import type { AssignedShipperUsersPageParams } from 'src/pages/shippers/details/information/shipper-users-table/use-page-params';
import { userAssignmentStatusValues } from 'src/pages/shippers/organisations-and-users/schema';
import { useDocumentExchangeRequest } from 'src/pages/shippers/upload-documents/use-document-exchange';

type Props = {
  contractManagement?: ContractManagementResponse;
  contractManagementShipper?: ShipperResponse | '';
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
  organisationService: Successful<OrganisationServiceRequest>;
};

export const ShipperTabs: FC<Props> = ({
  contractManagement,
  contractManagementShipper,
  shipperPlatformIds,
  organisationService,
}) => {
  const { pathname, hash } = useLocation();
  const organisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });
  const shipperRefId = shipperPlatformIds?.response.data.referenceId.toString();
  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const documentExchange = useDocumentExchangeRequest();

  if (!documentExchange.response || !organisationDetails.response)
    return <PageSpinner />;

  const showDocumentExchange =
    documentExchange.response.status !== 404 &&
    documentExchange.response.data._links.getOperatorDocumentsOutbox?.href;

  return (
    <Tabs>
      <Link
        to={{
          pathname: `/dashboard/shippers/${shipperUuid}/information`,
          search: stringifySearchParams({
            userAssignmentStatus: userAssignmentStatusValues.filter(
              (status) => status !== 'NOT_REQUESTED'
            ),
          } satisfies Partial<AssignedShipperUsersPageParams['filterValues']>),
        }}
      >
        Shipper Overview
      </Link>

      {monolithUser?.isOperator && (
        <Link
          isActive={
            pathname === `/dashboard/shippers/${shipperUuid}/platform/` &&
            hash.startsWith(`#/credit-limits/${shipperRefId}`)
          }
          to={`/dashboard/shippers/${shipperUuid}/platform/#/credit-limits/${shipperRefId}`}
        >
          Credit Limits
        </Link>
      )}

      {organisationService.response.data._links.getAssignedShipperDetails && (
        <Link to={`/dashboard/shippers/${shipperUuid}/contact-details`}>
          Contact Details
        </Link>
      )}

      {/* adding a check for document exchange here as there might be a document exchange link for operator,
      which is not known to the monolith. If the operator is known to the monolith, then we show section anyway */}
      {(showDocumentExchange || monolithUser?.isOperator) && (
        <Link to={`/dashboard/shippers/${shipperUuid}/documents`}>
          Documents
        </Link>
      )}

      {contractManagement?._links.contracts && (
        <Link to={`/dashboard/shippers/${shipperUuid}/contracts`}>
          Contracts
        </Link>
      )}

      {contractManagementShipper &&
        contractManagementShipper._links.getFinancialSecurities && (
          <Link to={`/dashboard/shippers/${shipperUuid}/financial-securities`}>
            Financial Securities
          </Link>
        )}
    </Tabs>
  );
};
